import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { financeApi } from '@/lib/api/finance';
import { useI18n } from '@/i18n';

const yuan = (cents: number) =>
  (cents / 100).toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' });

const PIE_COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#64748b',
];

export function FinanceOverviewTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['finance-overview'],
    queryFn: financeApi.getOverview,
  });
  const sync = useMutation({
    mutationFn: financeApi.triggerSync,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
      qc.invalidateQueries({ queryKey: ['finance-expenses'] });
      qc.invalidateQueries({ queryKey: ['finance-contributions'] });
    },
  });

  if (isLoading || !data) {
    return <div className="p-6 text-gray-500">{t('common.loading')}</div>;
  }

  const chartData = data.monthly.map((m) => ({
    month: m.month,
    [t('financePage.revenue')]: m.revenue_cny_cents / 100,
    [t('financePage.expense')]: m.expense_cny_cents / 100,
    [t('financePage.net')]: m.net_cny_cents / 100,
  }));
  const cashflowData = data.monthly.map((m) => ({
    month: m.month,
    pool: m.cumulative_pool_cny_cents / 100,
  }));
  const pieData = Object.entries(data.expense_by_category).map(([name, cents]) => ({
    name,
    value: cents / 100,
  }));
  const channelData = Object.entries(data.revenue_by_channel).map(([name, cents]) => ({
    name,
    value: cents / 100,
  }));

  return (
    <div className="space-y-6">
      {(!data.reconciliation_ok || data.range_truncated) && (
        <div className="rounded-lg border border-red-300 bg-red-50 text-red-800 p-3 text-sm">
          <span className="font-medium">⚠ {t('financePage.reconWarn')}</span>
          {data.reconciliation_note ? `：${data.reconciliation_note}` : ''}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {t('financePage.lastSync')}: {data.last_sync_finished_at ?? '—'}（{data.last_sync_status}）
        </div>
        <button
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
        >
          {sync.isPending ? t('financePage.syncing') : t('financePage.syncNow')}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card label={t('financePage.committed')} value={yuan(data.committed_cny_cents)} />
        <Card label={t('financePage.received')} value={yuan(data.received_cny_cents)} />
        <Card
          label={t('financePage.outstanding')}
          value={yuan(data.outstanding_cny_cents)}
          sub={t('financePage.outstandingHint')}
        />
        <Card
          label={t('financePage.capitalPool')}
          value={yuan(data.capital_pool_cny_cents)}
          highlight
          sub={t('financePage.capitalPoolHint')}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card label={t('financePage.totalExpense')} value={yuan(data.total_expense_cny_cents)} />
        <Card label={t('financePage.totalRevenue')} value={yuan(data.total_revenue_cny_cents)} />
        <Card
          label={t('financePage.monthlyBurn')}
          value={`${yuan(data.monthly_burn_cny_cents)} / ${t('financePage.perMonth')}`}
        />
        <Card
          label={t('financePage.runway')}
          value={
            data.runway_months == null
              ? t('financePage.breakeven')
              : `${data.runway_months} ${t('financePage.months')}`
          }
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-medium mb-3">{t('financePage.monthlyPnl')}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey={t('financePage.revenue')} fill="#22c55e" />
              <Bar dataKey={t('financePage.expense')} fill="#ef4444" />
              <Bar dataKey={t('financePage.net')} fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-medium mb-3">{t('financePage.cashflow')}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={cashflowData}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="pool"
                name={t('financePage.capitalPool')}
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border p-4 overflow-x-auto">
          <h3 className="font-medium mb-3">{t('financePage.accounts')}</h3>
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th>{t('financePage.account')}</th>
                <th className="text-right">{t('financePage.accountContribution')}</th>
                <th className="text-right">{t('financePage.managedReceived')}</th>
                <th className="text-right">{t('financePage.spent')}</th>
                <th className="text-right">{t('financePage.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((a) => (
                <tr key={a.account} className="border-t">
                  <td className="py-1">
                    {a.account}
                    {a.owner && <span className="text-gray-400 ml-1">· {a.owner}</span>}
                  </td>
                  <td className="text-right text-gray-500">{yuan(a.contribution_cny_cents)}</td>
                  <td className="text-right text-gray-500">{yuan(a.received_cny_cents)}</td>
                  <td className="text-right text-gray-500">{yuan(a.spent_cny_cents)}</td>
                  <td
                    className={`text-right font-medium ${
                      a.balance_cny_cents < 0 ? 'text-red-600' : ''
                    }`}
                  >
                    {yuan(a.balance_cny_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-medium mb-3">{t('financePage.byCategory')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                {pieData.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-medium mb-3">{t('financePage.byChannel')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={channelData} dataKey="value" nameKey="name" outerRadius={80}>
                {channelData.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Card({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
      <div className={`text-xs ${highlight ? 'text-blue-700' : 'text-gray-500'}`}>{label}</div>
      <div className={`text-lg font-semibold mt-1 ${highlight ? 'text-blue-800' : ''}`}>{value}</div>
      {sub && (
        <div className={`text-xs mt-1 ${highlight ? 'text-blue-500' : 'text-gray-400'}`}>{sub}</div>
      )}
    </div>
  );
}
